import { useRef, useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { importCsv } from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";

const SAMPLE = `bed,crop,variety,plants,sow_date,harvest_start,sun,length_cm,width_cm,row,col,stage
Bed A,Lettuce,Little Gem,8,2026-04-01,2026-05-25,full sun,240,120,1,1,planned
Bed A,Carrot,Nantes,40,2026-04-15,2026-07-01,full sun,240,120,2,1,planned
Greenhouse,Tomato,Cherokee Purple,4,2026-03-10,2026-07-15,full sun,300,150,1,1,planned`;

export default function CsvImport() {
    const { activeGarden } = useGardens();
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    const submit = async () => {
        if (!file) return;
        if (!activeGarden) {
            toast.error("Select a garden first");
            return;
        }
        setImporting(true);
        setResult(null);
        try {
            const r = await importCsv(activeGarden.id, file);
            setResult(r);
            toast.success(`Imported ${r.crops_created} crops · ${r.beds_created} new beds`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setImporting(false);
        }
    };

    const downloadSample = () => {
        const blob = new Blob([SAMPLE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "rooteed-sample.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-10" data-testid="import-page">
            <PageHeader
                overline="Migration"
                title="Import a CSV"
                description="Bring data from VegPlotter, Garden Planner or your own spreadsheet. New beds will be created automatically."
            />

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 journal-card p-6 md:p-8">
                    <div
                        onClick={() => inputRef.current?.click()}
                        className="border-2 border-dashed border-[var(--vm-border)] rounded-lg p-10 text-center cursor-pointer hover:border-[var(--vm-sage)] transition-colors"
                        data-testid="import-dropzone"
                    >
                        <Upload className="w-8 h-8 mx-auto text-[var(--vm-accent)] mb-3" strokeWidth={1.5} />
                        <p className="font-serif-display text-2xl text-[var(--vm-text)] mb-1">
                            {file ? file.name : "Choose a CSV file"}
                        </p>
                        <p className="text-sm text-[var(--vm-text-secondary)]">
                            {file ? `${(file.size / 1024).toFixed(1)} KB · ready to import` : "Click to browse or drop here"}
                        </p>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="import-file-input"
                            onChange={e => setFile(e.target.files?.[0] || null)}
                        />
                    </div>
                    <div className="flex flex-wrap gap-3 mt-6">
                        <Button
                            onClick={submit}
                            disabled={!file || importing}
                            data-testid="import-submit-btn"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6"
                        >
                            {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4 mr-2" /> Import</>}
                        </Button>
                        <Button variant="ghost" onClick={downloadSample} data-testid="import-sample-btn">
                            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download sample CSV
                        </Button>
                    </div>

                    {result && (
                        <div className="mt-6 p-4 rounded-lg bg-[var(--vm-input)] border border-[var(--vm-border)]" data-testid="import-result">
                            <div className="flex items-center gap-2 text-[var(--vm-accent)] font-medium">
                                <CheckCircle2 className="w-5 h-5" /> Import complete
                            </div>
                            <div className="text-sm text-[var(--vm-text-secondary)] mt-2">
                                {result.beds_created} bed(s) created · {result.crops_created} crop(s) added
                            </div>
                            {result.errors?.length > 0 && (
                                <div className="mt-3">
                                    <div className="text-xs flex items-center gap-1 text-[var(--vm-secondary)] mb-1">
                                        <AlertTriangle className="w-3 h-3" /> Skipped rows:
                                    </div>
                                    <ul className="text-xs text-[var(--vm-text-muted)] list-disc pl-5">
                                        {result.errors.slice(0, 10).map((e) => <li key={e}>{e}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <aside className="journal-card p-6 md:p-8 text-sm leading-relaxed">
                    <div className="overline text-[var(--vm-sage)] mb-2">Recognised columns</div>
                    <ul className="space-y-1 text-[var(--vm-text-secondary)]">
                        <li><b>bed</b> · bed name (auto-created)</li>
                        <li><b>crop</b> · crop name (required)</li>
                        <li><b>variety</b> · cultivar</li>
                        <li><b>plants</b> · plant count</li>
                        <li><b>sow_date</b> · YYYY-MM-DD</li>
                        <li><b>transplant_date</b> · YYYY-MM-DD</li>
                        <li><b>harvest_start</b> · YYYY-MM-DD</li>
                        <li><b>type</b> · raised bed / greenhouse / polytunnel / container</li>
                        <li><b>length_cm, width_cm</b></li>
                        <li><b>sun</b> · full sun / partial shade / shade</li>
                        <li><b>row, col</b> · grid position</li>
                        <li><b>stage</b> · planned / sown / …</li>
                        <li><b>notes</b></li>
                    </ul>
                    <div className="mt-4 text-xs text-[var(--vm-text-muted)]">Headers are case-insensitive. Unknown columns are ignored.</div>
                </aside>
            </div>
        </div>
    );
}
